import type { NightmareType } from './nightmares.ts';
import { sec } from './units.ts';

/**
 * Group expeditions (docs/EXPEDICIONES.md): a long run under the shell that only opens when exactly a given
 * number of guardians are connected. The first is the garrison cellars, for 3 to 8 heroes: together down to
 * the great floodgate, apart through the wings (each wing's second stage unlocked by another wing's first, in a
 * circle), and together again to raise the gate bell up the well.
 */
export const CELLARS = {
  minGroup: 8,
  minHeroes: 3,
  maxHeroes: 8,
  /** Rooms of the layout (hex centre, radius). Wing B rooms hold the wing's lock. */
  rooms: {
    entry: { q: 0, r: -18, radius: 2 },
    guard: { q: 0, r: -10, radius: 3 },
    hub: { q: 0, r: 0, radius: 3 },
    cisternA: { q: -10, r: 0, radius: 3 },
    cisternB: { q: -19, r: 0, radius: 2 },
    towerA: { q: 10, r: 0, radius: 3 },
    towerB: { q: 19, r: 0, radius: 2 },
    powderA: { q: -10, r: 10, radius: 3 },
    powderB: { q: -19, r: 19, radius: 2 },
    cells: { q: 10, r: -10, radius: 3 },
    cistern: { q: 0, r: 11, radius: 4 },
  },
  /** Nightmare points per hero for each room's waves, before level scaling. */
  points: { guard: [16, 22], wingA: 12, wingB: 12, press: 6, final: 12 },
  /** The old garrison's sergeants with the guard room's second wave: inked elites, by party size (heroes 1 to 8), tougher than their kind. */
  guardElites: { kind: 'skeleton' as NightmareType, count: [0, 1, 1, 2, 3, 3, 4, 4, 5], hpBp: 100000 },
  /** While someone works a wheel, a lever or the winch, a few come every this often. */
  pressEvery: sec(10),
  /** Party-size scaling (heroes 1 to 8; past 4, BIG_PARTY) and level scaling, like the crypt. */
  partyBp: [0, 10000, 17000, 26000, 36000],
  /** 9 % until the class balance (docs/BALANCE_CLASES.md): skills grow with the level; 22 % keeps cellars-4p at least as hard (bots 46 %). */
  perLevelBp: 2200,
  /**
   * Past `UNIQUE_EASE.fromLevel`, each level more adds this instead (docs/UNICAS.md §5: the armour and amulet come later).
   * Only 9 real runs: halfway to what balance:mirror asked; re-measure with real runs.
   */
  easedPerLevelBp: 1000,
  /** Past four heroes (BIG_PARTY): a gentler slope, the ink sets the pace down here. */
  big: { extraBp: 13000, rampBp: 0 },
  inkBp: 3000,
  bestiary: { goblin: 3, skeleton: 3, wolf: 2, slinger: 2, wraith: 1, slime: 1 } as Partial<Record<NightmareType, number>>,
  /** What sleeps beside the bell: an elite ogre, tougher than its kind by party size (heroes 1 to 4; past 4, BIG_PARTY). */
  guardian: { kind: 'ogre' as NightmareType, hpBp: [0, 30000, 45000, 70000, 120000] },
  /** Standing by a device works it: wheels and the tower lever this long; the wing locks too. */
  device: { range: 750, wheelTicks: sec(12), leverTicks: sec(12), lockTicks: sec(6) },
  /** Carrying: picking up within reach; a carrier walks slower and holds its blows (skills still work). */
  carry: { reach: 800, speedBp: 6500, bellSpeedBp: 4000, bellLeash: 900 },
  /** A powder barrel: dropped by the rubble it lights; it bursts after the fuse. Struck too often, it goes off in hand. */
  barrel: { hp: 60, fuse: sec(3), blastRadius: 1600, blastDamage: 60, rubbleRange: 1500 },
  /** The bell back on the winch's hook, then someone cranks this long. */
  winch: { range: 900, ticks: sec(60) },
  /**
   * While the winch turns, the floodgate hall is the last stand: a press this often, growing from the first
   * to the last points per hero as the bell comes up, this share of it inked, and every `eliteEvery`-th press
   * the old garrison's sergeants (by party size, heroes 1 to 8) with this much life over their kind.
   * A party above `levelFrom` (the level the cellars open at) meets them tougher: `perLevelHpBp` more life per
   * level of the party's average, and one more sergeant every `elitePerLevels` levels. More of them alone doesn't
   * do it: at level 13 a bigger press still dies to one blow.
   */
  winchPress: {
    every: sec(8),
    points: [14, 30],
    inkBp: 5000,
    eliteEvery: 2,
    elites: [0, 1, 1, 1, 2, 2, 2, 3, 3],
    eliteHpBp: 50000,
    levelFrom: 8,
    perLevelHpBp: 12000,
    elitePerLevels: 1,
  },
  /** Fallen alone: down this long unless an ally comes; then up at the floodgate with this share of life. */
  downTicks: sec(30),
  respawnHpBp: 5000,
  /** The ink rises once the floodgate opens: the time to bring the bell up; every fall shortens it. */
  inkTicks: sec(200),
  fallInkTicks: sec(15),
  /** A disconnected hero (ghost) has this long to come back before the expedition is lost. */
  dropGraceTicks: sec(45),
  /** The bell on the keep: it hangs there from the first win; the garrison's banners come with this many, the red sash with a win nobody fell in. */
  trophy: { bannersAt: 5 },
  /** The day's chest, once per hero. */
  reward: { xp: 900, dust: 150, relicBp: 4000 },
  /** Lost runs: XP per wing lock opened. */
  xpPerLock: 120,
};
